import QueryString from 'query-string';
import { AemConfigResponse, FlowConfig } from '../helpers/RequestResponse';
import ColorLogoMobile from '@dls/assets/images/logo_singtel@2x.png';
import ColorLogoDesktop from '@dls/assets/images/logo_singtel@3x.png';
import WhiteLogo from '@dls/assets/images/logo_singtel_white@3x.png';

// Theme asset configuration - centralized for easy extension
interface ThemeAssets {
  logo: {
    desktop: string;
    mobile: string;
  };
  background?: string;
  darkModeLogo?: {
    desktop: string;
    mobile: string;
  };
}

const THEME_ASSETS: Record<string, ThemeAssets> = {
  GOMOSMB: {
    logo: {
      desktop: '/sso/gomosmb.svg',
      mobile: '/sso/gomosmb.svg',
    },
    background: '/sso/gomosmb.png',
  },
  // Add more themes here as needed
};

// Map flowIds directly to theme names for explicit control
const FLOW_ID_THEME_MAP: Record<string, string> = {
  'b3f1a8c4-29de-47e0-9fb2-6d94e3c2a8d7': 'GOMOSMB',
};

// Fallback: Map portal identifiers to theme names
const PORTAL_THEME_MAP: Record<string, string> = {
  GOMOSMB: 'GOMOSMB',
};

function resolveActiveFlow(flows: FlowConfig[], search?: string): FlowConfig | undefined {
  const qs = QueryString.parse(search || '');
  const flowId = (qs.flowId as string) || (qs.state && (qs.state as string).split('--')[2]);
  return flowId ? flows.find((f) => f.flowId === flowId) : flows[0];
}

export function getThemeNameFromConfig(
  aemConfig: AemConfigResponse,
  search?: string
): string | undefined {
  const flows = aemConfig?.onepassFlows || [];
  const activeFlow = resolveActiveFlow(flows, search);
  
  // Priority 1: Check flowId mapping first (most explicit)
  if (activeFlow?.flowId && FLOW_ID_THEME_MAP[activeFlow.flowId]) {
    return FLOW_ID_THEME_MAP[activeFlow.flowId];
  }
  
  // Priority 2: Check portal mapping (fallback)
  const portal = activeFlow?.portal;
  return portal && PORTAL_THEME_MAP[portal] ? PORTAL_THEME_MAP[portal] : undefined;
}

export function getThemeAssets(themeName?: string, mode?: string): ThemeAssets {
  // Return theme-specific assets if available
  if (themeName && THEME_ASSETS[themeName]) {
    const themeConfig = THEME_ASSETS[themeName];
    // Use dark mode variant if available and mode is dark
    if (mode === 'dark' && themeConfig.darkModeLogo) {
      return {
        logo: themeConfig.darkModeLogo,
        background: themeConfig.background,
      };
    }
    return themeConfig;
  }
  
  // Default Singtel branding
  const desktop = mode === 'dark' ? WhiteLogo : ColorLogoDesktop;
  return {
    logo: {
      desktop,
      mobile: ColorLogoMobile,
    },
    background: '/sso/background.png',
  };
}

export function getThemeBackground(themeName?: string): string {
  if (themeName && THEME_ASSETS[themeName]?.background) {
    return THEME_ASSETS[themeName].background!;
  }
  return '/sso/background.png';
}

export { PORTAL_THEME_MAP, FLOW_ID_THEME_MAP, THEME_ASSETS };